// Theme registry — user-selectable named palettes (IDE/terminal-style).
//
// The canonical design-tokens.ts still owns the Metria "brand" palette
// (its `palette`/`intensityScale`/`heatmapRamp` exports stay the single source
// for anything that doesn't opt into theming). This module widens that into a
// FAMILY × MODE registry: each family fills the exact same ~30 role contract
// (20 palette roles + 5-band intensity scale + 5-step heatmap ramp) in both a
// light and a dark mode, so the resolver can pick `themes[family][mode]`.
//
// Every non-brand family was mapped from a well-known scheme and AA-validated
// (≥4.5:1 on every contract-mandated text pair, both modes) before landing here
// — see scratchpad/{catppuccin,rosepine}-notes.md for the per-pair tables.
//   • catppuccin — Latte (light) / Mocha (dark), accent = peach
//   • rosepine   — Dawn (light) / Rosé Pine (dark), accent = rose
//
// Adding a family = add a ThemeDefinition here (+ its label in THEME_FAMILIES);
// nothing else in shared changes.

import {
  palette,
  intensityScale,
  heatmapRamp,
  effortRamp,
  type Palette,
  type IntensityScale,
} from "./design-tokens.js";

type Hex = string;

/** The 20 named palette roles, widened from the brand literal types. */
export type ThemePalette = Record<keyof Palette, Hex>;

type IntensityBand = { fill: Hex; on: Hex; soft: Hex; text: Hex };

/** The 5 intensity bands (work/moderate/heavy/max/none), each 4 sub-roles. */
export type ThemeIntensity = Record<keyof IntensityScale, IntensityBand>;

/** One effort-meter step: segment fill + the step number's ink (per-stop
 *  because a monochrome light→dark band has no single AA-passing ink). */
export type EffortRampStop = { fill: Hex; on: Hex };

export type ThemeDefinition = {
  palette: { light: ThemePalette; dark: ThemePalette };
  intensityScale: { light: ThemeIntensity; dark: ThemeIntensity };
  heatmapRamp: { light: readonly Hex[]; dark: readonly Hex[] };
  effortRamp: {
    light: readonly EffortRampStop[];
    dark: readonly EffortRampStop[];
  };
};

// Brand = the existing canonical tokens, unchanged (referenced, not copied, so
// design-tokens.ts stays the one place brand hues are edited).
const brand: ThemeDefinition = { palette, intensityScale, heatmapRamp, effortRamp };

const catppuccin: ThemeDefinition = {
  palette: {
    // Accent = Catppuccin's SIGNATURE mauve (not peach — peach ≈ JL brand orange,
    // which defeats the point in light mode; Antenore 2026-07-01). White on Latte
    // mauve #8839ef = 5.42:1 AA-pass; Mocha mauve #cba6f7 takes near-black text.
    light: {
      brand500: "#8839ef", brand600: "#7526d3", brand100: "#e7dbfb",
      // DS-11/DS-21 (see design-tokens.ts): Latte mauve already reads as text
      // and already takes white, so this family doesn't move a pixel.
      brandText: "#7526d3", brandPressed: "#7526d3", inkOnBrandPressed: "#ffffff",
      bg: "#eff1f5", surface: "#e6e9ef", surface2: "#dce0e8",
      border: "#acb0be", borderStrong: "#767a8e", scrim: "rgba(0,0,0,0.5)",
      // DS-8 (2026-07-14): ink2 was Latte subtext0 #6c6f85 = 3.73:1 on surface2,
      // under the floor on the DEFAULT theme, while ink3 held Latte subtext1 —
      // which is the STRONGER of the two. The ladder was inverted: tertiary text
      // outranked secondary. ink2 now sits between text and subtext1, so
      // ink > ink2 > ink3 reads in that order and all three clear AA.
      //
      // DS-22 (Antenore, 2026-07-26) — comfort pass, route C2. The three inks
      // darken; the SURFACES do not move. Primary sat at 6.04:1, i.e. under
      // AAA on the family the phones actually run, because here `surface2` is
      // the DARKEST surface and squeezes text from above. 6.04 -> 7.07.
      //
      // Route C1 (lighten the surfaces instead) was considered and rejected:
      // it would flatten surface<->surface2 to ~1.02, eating the very border
      // separation VB-PAL-4 exists to save. Darkening the ink costs nothing
      // structurally, and the ladder gets MORE legible as a ladder, not less
      // (steps on surface2 go 0.77/0.54 -> 1.46/0.80, against a gate of 0.30).
      // He is myopic and reads this theme by default: "legible but barely" is
      // not the bar ([[feedback_user_visual_accessibility]]).
      ink: "#43455c", ink2: "#50546d", ink3: "#5b5e75", inkOnBrand: "#ffffff",
      inkOnDanger: "#ffffff", inkOnWarn: "#ffffff",
      pr: "#865511", prFill: "#ece1d2",
      complete: "#286419", completeFill: "#d3e4d5", // DS-13: was #2d701e (4.60 on surface2, borderline) → 5.42, comfortable floor for reduced vision
      danger: "#b20d30", dangerFill: "#eacdd7",
      warn: "#865511", warnFill: "#ece1d2",
    },
    dark: {
      brand500: "#cba6f7", brand600: "#b799ed", brand100: "#2e2540",
      // DS-11/DS-21: Mocha mauve, unchanged — text 4.83 worst, pressed 7.85.
      brandText: "#b799ed", brandPressed: "#b799ed", inkOnBrandPressed: "#11111b",
      // DS-8 comfort (2026-07-14, Antenore approved): surface2 was #45475a —
      // the LIGHTEST tone in the dark stack, so it squeezed body text from
      // above and capped even the PRIMARY ink at 6.31:1, under AAA. The inks
      // were never the problem. Darkening it toward `surface` keeps the stack
      // (bg < surface < surface2) and lifts all three tiers at once:
      // 6.31/4.10/5.15 -> 7.98/7.07/6.52. He reads dark, and "legible but
      // barely" is not the bar ([[feedback_user_visual_accessibility]]).
      // NOTE: #363849 is NOT one of the official 26 Mocha swatches (the old
      // #45475a was Mocha `surface1`). Hue/saturation are held — it interpolates
      // on the family ramp between surface0 and surface1 — but this family is no
      // longer byte-for-byte upstream Catppuccin here. Same trade DS-9/DS-13
      // already made elsewhere: the floor wins over swatch fidelity.
      bg: "#1e1e2e", surface: "#313244", surface2: "#363849",
      border: "#585b70", borderStrong: "#81869d", scrim: "rgba(0,0,0,0.5)",
      // DS-8: same inversion in Mocha — ink2 was subtext0 (4.10 on surface2),
      // ink3 subtext1. ink2 now sits between text and subtext1 (5.59 worst).
      ink: "#cdd6f4", ink2: "#c2cae4", ink3: "#bac2de", inkOnBrand: "#11111b",
      inkOnDanger: "#11111b", inkOnWarn: "#11111b",
      pr: "#f9e2af", prFill: "#413d43",
      complete: "#a6e3a1", completeFill: "#343e40",
      danger: "#f38ba8", dangerFill: "#402f42",
      warn: "#f9e2af", warnFill: "#413d43",
    },
  },
  intensityScale: {
    light: {
      work: { fill: "#a9d1a4", on: "#11111b", soft: "#d3e4d5", text: "#2d701e" },
      moderate: { fill: "#e9c99f", on: "#11111b", soft: "#ece1d2", text: "#865511" },
      heavy: { fill: "#ebacb4", on: "#11111b", soft: "#eed5db", text: "#ac343e" },
      max: { fill: "#c6a7f3", on: "#11111b", soft: "#dfd4f4", text: "#7a33d7" },
      none: { fill: "#bcc0cc", on: "#11111b", soft: "#ccd0da", text: "#4c4f69" },
    },
    dark: {
      work: { fill: "#4e6356", on: "#ffffff", soft: "#343e40", text: "#a6e3a1" },
      moderate: { fill: "#6b635b", on: "#ffffff", soft: "#413d43", text: "#f9e2af" },
      heavy: { fill: "#664c5a", on: "#ffffff", soft: "#3f3342", text: "#eba0ac" },
      max: { fill: "#5b4e74", on: "#ffffff", soft: "#3a344e", text: "#cba6f7" },
      // fill/soft track surface2/surface (the contract rosepine already keeps
      // in both modes). They held the OLD surface2 #45475a by value, so the
      // DS-8 comfort pass left the "no band" cell 1.27:1 brighter than every
      // other neutral chip on the same screen.
      none: { fill: "#363849", on: "#ffffff", soft: "#313244", text: "#cdd6f4" },
    },
  },
  // Semantic rested→over-used ramp on Catppuccin accents (green→gold→peach→red),
  // stops spread for perceptible low/mid separation.
  heatmapRamp: {
    light: ["#40a02b", "#8bb32a", "#df8e0d", "#fe640b", "#d20f39"],
    dark: ["#a6e3a1", "#c4d96a", "#f9e2af", "#fab387", "#eb6f7d"],
  },
  // Monochrome effort ramp on the signature mauve (see design-tokens.ts
  // effortRamp for the semantics; pairs AA-validated by effortRampContrast).
  effortRamp: {
    light: [
      { fill: "#e3d2f9", on: "#1f0f33" },
      { fill: "#c4a1f2", on: "#1f0f33" },
      { fill: "#a067e9", on: "#1f0f33" },
      { fill: "#6e1fd6", on: "#ffffff" },
      { fill: "#4c1593", on: "#ffffff" },
    ],
    dark: [
      { fill: "#371268", on: "#ece8f3" },
      { fill: "#4f1a93", on: "#ece8f3" },
      { fill: "#6822c3", on: "#ece8f3" },
      { fill: "#9d67e4", on: "#180d26" },
      { fill: "#bc97ed", on: "#180d26" },
    ],
  },
};

const rosepine: ThemeDefinition = {
  palette: {
    light: {
      brand500: "#d7827e", brand600: "#8c5452", brand100: "#fae8e1",
      // DS-11/DS-21 — this is the family the split exists for. The fill stays
      // exactly where it was, but its ink flips: `inkOnBrand` #312e44 on this
      // dark rose is 2.18:1, so pressing a brand button here USED TO ERASE ITS
      // OWN LABEL. Dawn's own near-white takes it to 5.77 and keeps the family
      // warmth, the same move IB-11 already made for danger/warn below.
      brandText: "#8c5452", brandPressed: "#8c5452", inkOnBrandPressed: "#fffaf3",
      bg: "#faf4ed", surface: "#fffaf3", surface2: "#f2e9e1",
      // borderStrong: DS-9 (was #cecacd -> 1.56 vs surface) + DS-23 retune vs
      // surface2, the surface that actually decides this token (see design-tokens.ts)
      border: "#dfdad9", borderStrong: "#858184", scrim: "rgba(0,0,0,0.5)",
      // DS-8: ink2 #6a6688 and ink3 #6a6586 were the SAME colour give or take
      // three points, and inverted by a hair (4.53 vs 4.59) — Dawn's own subtle
      // (3.67) and muted (2.48) are both under the floor, so both had been
      // darkened until they collided. ink2 backs off to a real second step:
      // three levels that read apart and all clear AA.
      //
      // DS-22 (Antenore, 2026-07-26) — comfort pass, route C2, the same move
      // as Latte above and for the same reason: primary was 6.07:1, under AAA,
      // because Dawn's `surface2` is its darkest surface. 6.07 -> 7.03, ladder
      // steps 0.89/0.59 -> 1.42/0.80. Surfaces untouched (see the C1 note in
      // catppuccin-light for why lightening them was the wrong lever).
      ink: "#4e496c", ink2: "#5c577f", ink3: "#676282", inkOnBrand: "#312e44",
      // IB-11: brand500 #d7827e is a LIGHT rose, so inkOnBrand is dark. But
      // danger/warn are DARK fills and need the opposite ink. Dawn's own
      // near-white (its `surface`) keeps the family warmth: 5.51 on danger,
      // 5.24 on warn, against 2.29/2.41 when they reused inkOnBrand.
      inkOnDanger: "#fffaf3", inkOnWarn: "#fffaf3",
      pr: "#8f6020", prFill: "#fbead8",
      complete: "#3a656d", completeFill: "#eaeae5", // DS-13: was #406f77 (4.66 on surface2, borderline) → 5.37, comfortable floor for reduced vision
      danger: "#955265", dangerFill: "#f6e4e0",
      warn: "#8f6020", warnFill: "#fbead8",
    },
    dark: {
      brand500: "#ebbcba", brand600: "#a28286", brand100: "#2f2834",
      // DS-11/DS-21: the pressed fill is brand600 untouched, but brandText is a
      // touch lighter than it — brand600 itself reads 4.12 on `brand100` and
      // 4.39 on `surface2`, i.e. under the floor on BOTH, which is precisely
      // the reading job it was failing. #a88a8d clears it (4.54 / 4.83).
      brandText: "#a88a8d", brandPressed: "#a28286", inkOnBrandPressed: "#191724",
      bg: "#191724", surface: "#1f1d2e", surface2: "#26233a",
      // borderStrong: DS-9 (was #524f67 -> 2.10 vs surface) + DS-23 retune vs surface2
      border: "#403d52", borderStrong: "#74718b", scrim: "rgba(0,0,0,0.5)",
      // DS-8: same collision as Dawn — ink2 #908caa (Rosé Pine's own subtle) and
      // ink3 #8e8aa5 sat 0.14 apart, so the two lower tiers were one tier
      // wearing two names. Official `muted` #6e6a86 is 2.94, far under the
      // floor, which is why ink3 had already been lightened up into subtle's
      // lap. ink2 moves up instead: 11.51 > 6.09 > 4.57, with room to see it.
      // DS-8 comfort: primary already reads 11.51 here, so the headroom was
      // free — ink2/ink3 were just parked near the floor. 11.51 > 8.59 > 6.31.
      ink: "#e0def4", ink2: "#c4c0d9", ink3: "#a8a4c0", inkOnBrand: "#191724",
      inkOnDanger: "#191724", inkOnWarn: "#191724",
      pr: "#f6c177", prFill: "#30282c",
      complete: "#9ccfd8", completeFill: "#252937",
      danger: "#eb6f92", dangerFill: "#311f30",
      warn: "#f6c177", warnFill: "#30282c",
    },
  },
  intensityScale: {
    light: {
      work: { fill: "#286983", on: "#ffffff", soft: "#e5e6e2", text: "#575279" },
      moderate: { fill: "#ea9d34", on: "#000000", soft: "#fbead8", text: "#575279" },
      heavy: { fill: "#b4637a", on: "#000000", soft: "#f6e4e0", text: "#575279" },
      max: { fill: "#907aa9", on: "#000000", soft: "#f1e8e6", text: "#575279" },
      none: { fill: "#f2e9e1", on: "#000000", soft: "#f4ede8", text: "#6a6688" },
    },
    dark: {
      work: { fill: "#31748f", on: "#ffffff", soft: "#1a2030", text: "#e0def4" },
      moderate: { fill: "#f6c177", on: "#000000", soft: "#30282c", text: "#e0def4" },
      heavy: { fill: "#eb6f92", on: "#000000", soft: "#311f30", text: "#e0def4" },
      max: { fill: "#c4a7e7", on: "#000000", soft: "#2b2539", text: "#e0def4" },
      none: { fill: "#26233a", on: "#ffffff", soft: "#21202e", text: "#908caa" },
    },
  },
  // Rosé Pine has no true green, so the low end starts cool (foam/pine) and
  // warms through gold→rose→love — same low→high meaning, stops kept distinct.
  heatmapRamp: {
    light: ["#56949f", "#90a36a", "#ea9d34", "#d97e6a", "#b4637a"],
    dark: ["#9ccfd8", "#bcc78a", "#f6c177", "#ea9d8f", "#eb6f92"],
  },
  // Monochrome effort ramp on the rose accent (see design-tokens.ts
  // effortRamp for the semantics; pairs AA-validated by effortRampContrast).
  effortRamp: {
    light: [
      { fill: "#f1dcda", on: "#33110f" },
      { fill: "#e0b1ae", on: "#33110f" },
      { fill: "#cd7f7a", on: "#33110f" },
      { fill: "#b14b43", on: "#ffffff" },
      { fill: "#7a332e", on: "#ffffff" },
    ],
    dark: [
      { fill: "#5f201c", on: "#f3e9e8" },
      { fill: "#862d27", on: "#f3e9e8" },
      { fill: "#b63d35", on: "#f3e9e8" },
      { fill: "#d16861", on: "#260e0d" },
      { fill: "#e19d98", on: "#260e0d" },
    ],
  },
};

// Family keys as a literal tuple so the zod enum (schemas/profile.ts) and the
// registry stay a single source of truth. Adding a family = extend this tuple +
// add its ThemeDefinition below; the schema/API pick it up for free.
export const THEME_FAMILY_KEYS = ["brand", "catppuccin", "rosepine"] as const;
export type ThemeFamily = (typeof THEME_FAMILY_KEYS)[number];

/** User-facing mode preference. `system` follows the OS colour scheme. */
export const THEME_MODES = ["system", "light", "dark"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const themes: Record<ThemeFamily, ThemeDefinition> = { brand, catppuccin, rosepine };

/** The persisted user preference (Profile.themePreference). */
export type ThemePreference = { family: ThemeFamily; mode: ThemeMode };

/** Ordered list for the Settings picker (label = display name). */
export const THEME_FAMILIES: { key: ThemeFamily; label: string }[] = [
  { key: "catppuccin", label: "Catppuccin" },
  // ⭐ La CHIAVE resta `brand`: e' il valore persistito in Profile.themePreference,
  // quindi il rebrand non migra niente. Cambia solo l'etichetta, che e' display-only.
  { key: "brand", label: "Metria" },
  { key: "rosepine", label: "Rosé Pine" },
];

export const DEFAULT_THEME_FAMILY: ThemeFamily = "catppuccin";
export const DEFAULT_THEME_MODE: ThemeMode = "system";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = {
  family: DEFAULT_THEME_FAMILY,
  mode: DEFAULT_THEME_MODE,
};

/** Narrowing guards for values coming off the wire / persisted preference. */
export function isThemeFamily(v: unknown): v is ThemeFamily {
  return typeof v === "string" && (THEME_FAMILY_KEYS as readonly string[]).includes(v);
}
export function isThemeMode(v: unknown): v is ThemeMode {
  return typeof v === "string" && (THEME_MODES as readonly string[]).includes(v);
}

/** Resolve the effective dark flag from a mode + the OS colour scheme. */
export function resolveDark(mode: ThemeMode, osDark: boolean): boolean {
  return mode === "system" ? osDark : mode === "dark";
}

/** Coerce an unknown (cache / wire) into a valid ThemePreference, field-wise. */
export function coerceThemePreference(v: unknown): ThemePreference {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return {
    family: isThemeFamily(o.family) ? o.family : DEFAULT_THEME_FAMILY,
    mode: isThemeMode(o.mode) ? o.mode : DEFAULT_THEME_MODE,
  };
}
